/**
 * The point the feed is ranked around, and how it was obtained.
 *
 * Two sources, deliberately: the browser's geolocation permission, and an address the
 * user typed (geocoded server-side by `/locations/search`). A permission prompt is not
 * something to spend on every visitor, and it cannot answer "what's on where I'm
 * travelling next month" — so neither source is a fallback for the other.
 *
 * Stored in localStorage rather than the URL: a home address is not something to put in
 * a link the user might share. The city filter stays in the URL and keeps working
 * exactly as before; a location only reorders what the city already selected.
 */

const STORAGE_KEY = 'applauss.userLocation';

// Offered radii, in km. `null` is "rank by distance but do not exclude anything", which
// is the honest default: a hard cut can empty a feed, and a user who has not asked for
// one should never see that happen.
export const RADIUS_OPTIONS = [null, 2, 5, 10, 25, 50];

/**
 * One radius in words — the picker's buttons and the chip row's chip say it the same way.
 * `t` first, like the other label helpers; any namespace's will do.
 */
export const radiusLabel = (t, radiusKm) => (radiusKm ? `${radiusKm} km` : t('labels:misc.anyDistance'));

/**
 * City names compare case-insensitively everywhere in this app — `resolveAvailableCity`
 * folds case because the backend resolves city names the same way, so a stored "Madrid"
 * and a requested "madrid" are the same city and must not be treated as a mismatch.
 *
 */
const sameCity = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

/**
 * Whether a stored location still belongs to the city being shown.
 *
 * One predicate for both readers, because there are two decisions riding on this and they
 * must not drift: this answers whether to *send* the location, and the page's clear-effect
 * whether to *delete* it. When those two disagreed, a casing difference made the page
 * throw away a location the request would have used quite happily.
 *
 * True when there is nothing to contradict — no city being fetched, or a location saved
 * before cities were stamped onto them.
 */
export const locationBelongsToCity = (location, city) => {
  if (!city || !location?.city) return true;
  return sameCity(location.city, city);
};

export const isUsableLocation = (location) =>
  !!location && Number.isFinite(location.lat) && Number.isFinite(location.lon);

/**
 * Read the stored location. Returns null for anything unusable — a half-written entry,
 * a value from an older shape, or a browser that refuses storage (private mode).
 */
export const readUserLocation = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isUsableLocation(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * The location a caller means: the one it passed, or the stored one when it passed nothing.
 *
 * `undefined` is "I hold no location" and falls back to storage; `null` is "none". One rule
 * for both readers that take an optional location — `getRecommendations` for what it sends
 * and `useMapDayEvents` for what it keys the day on — because the day refetches only when
 * the two agree (#595).
 */
export const resolveUserLocation = (passed) => (passed === undefined ? readUserLocation() : passed);

export const saveUserLocation = (location) => {
  if (!isUsableLocation(location)) return null;
  const stored = {
    lat: Number(location.lat),
    lon: Number(location.lon),
    label: location.label || '',
    radiusKm: Number.isFinite(location.radiusKm) ? location.radiusKm : null,
    source: location.source === 'device' ? 'device' : 'address',
    // The city the point was chosen in. A location only means something inside the city
    // whose events it ranks: "Gran Via" is a Madrid answer, and applying it to a Paris
    // feed ranks every Paris event by its distance from Madrid — or, with a radius,
    // empties the feed outright. Stored rather than inferred, because the feed's city
    // changes under a location that was saved earlier.
    city: location.city || null,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage refused (private mode, quota). The location still works for this
    // session — it just will not survive a reload.
  }
  return stored;
};

export const clearUserLocation = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do: an unreadable store is already "no stored location".
  }
};

/**
 * Ask the browser for the device's position.
 *
 * Rejects with a plain-language reason rather than a GeolocationPositionError, because
 * the caller shows it to a person: "denied" needs different copy from "unavailable",
 * and the numeric codes say nothing to a user.
 */
export const requestDeviceLocation = ({ timeoutMs = 10000 } = {}) =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          source: 'device',
        }),
      (error) => reject(new Error(error?.code === 1 ? 'denied' : 'unavailable')),
      { timeout: timeoutMs, maximumAge: 60000 },
    );
  });

/**
 * What to show for a geocoded place.
 *
 * `/locations/search` composes `short_label` from OpenStreetMap's structured fields —
 * "25 Boulevard de l'Ouest, 93340 Le Raincy" — because the town cannot be recovered from
 * the flat `display_name`: it lists the neighbourhood ("Gare") and the town ("Le Raincy")
 * adjacent, with nothing saying which is which. `label` is that full chain, kept as the
 * fallback for a response from before this shipped.
 */
export const placeLabel = (result) => result?.short_label || result?.label || '';

/**
 * The `/recommendations` params for a location, or `{}` when there is none.
 *
 * `radius_km` is only sent with a radius selected: the API treats it as a hard filter
 * that also excludes events whose venue has no coordinates, so sending a default would
 * silently shrink the catalogue for everyone who never opened the control.
 *
 * `city` is the city the feed is being fetched for. A location stored for a different one
 * is dropped rather than applied — see the `city` field in `saveUserLocation`.
 */
export const locationRequestParams = (location, city = null) => {
  if (!isUsableLocation(location)) return {};
  // A stored location belongs to the city it was chosen in. Enforced here rather than in
  // the pages, because seven call sites fetch this feed and three of them (the OAuth
  // prefetch, onboarding's two) hold no location of their own and read the stored one: a
  // guard in the pages that do hold one leaves those ranking one city's events around
  // another city's point.
  //
  // Compared case-insensitively, like every other city comparison in the app
  // (`resolveAvailableCity`, and the backend behind it). Only the recommendations page
  // stores the canonical casing; onboarding and the home redirect pass whatever they
  // hold, and an exact match would silently drop a location for the city it belongs to
  // — turning distance ranking off on those paths with nothing on screen to say so.
  if (!locationBelongsToCity(location, city)) return {};
  const params = { lat: location.lat, lon: location.lon };
  if (Number.isFinite(location.radiusKm) && location.radiusKm > 0) {
    params.radius_km = location.radiusKm;
  }
  return params;
};

/**
 * Whether a location change is a new pool — the one key for it, used by the page's
 * refetch effect and the map's day store alike.
 *
 * Built from `locationRequestParams`, not the object, so a new label on the same point, or
 * another city's point (which the request drops), is not a change.
 */
export const locationKey = (location, city = null) =>
  JSON.stringify(locationRequestParams(location, city));

/**
 * Great-circle distance in kilometres.
 *
 * The client half of the same maths the API uses (`shared/utils/geo.py::haversine_km`):
 * the map already holds both points, so asking the server how far away a pin is would be
 * a round trip for arithmetic. Exact on the sphere — a flat-earth approximation drifts
 * exactly where a city is widest.
 */
export const distanceKm = (from, to) => {
  if (!isUsableLocation(from) || !isUsableLocation(to)) return null;

  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lon - from.lon);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLon / 2) ** 2;

  return 2 * R * Math.asin(Math.sqrt(a));
};

/**
 * Whether a point is inside a city's event area — `{lat, lon, radiusKm}` from `/cities`,
 * which is `city_search_areas` and therefore the same radius ingestion collects events
 * within and `/locations/search` marks typed addresses against.
 *
 * True when there is no city area to measure against — the map and the picker both run
 * before `/cities` resolves, and a city the list does not hold has no boundary to be
 * outside of.
 *
 * The `radiusKm` half of that check is defence against a malformed payload, not a
 * supported state: `radius_km` is required on `/cities` and `NOT NULL` in the table behind
 * it. It stays because the alternative on a bad payload is worse — `km <= NaN` is false,
 * which would refuse *every* position — but it is not a state to design for, and nothing
 * should start relying on a city without a radius.
 */
export const isWithinCityArea = (point, cityArea) => {
  if (!isUsableLocation(cityArea) || !Number.isFinite(cityArea.radiusKm)) return true;
  const km = distanceKm(cityArea, point);
  return km === null || km <= cityArea.radiusKm;
};

// Brisk-but-normal walking pace, and the distance past which nobody is walking. Both
// are rules of thumb rather than a routing engine: streets are not straight lines, and
// "58 min walk" across a city reads as a joke instead of as information.
const WALKING_KMH = 4.8;
const MAX_WALKABLE_KM = 3;

/**
 * Walking minutes to a point, or null when there is no location or it is too far to walk.
 *
 * The one place that turns a distance into minutes — the map pin's compact "4 min", the
 * card's "4 min walk" and the sheet's full sentence all read it, so they cannot disagree
 * about what counts as walkable.
 */
export const walkMinutes = (from, to) => {
  const km = distanceKm(from, to);
  if (km === null || km > MAX_WALKABLE_KM) return null;
  return Math.max(1, Math.round((km / WALKING_KMH) * 60));
};

// "6.2 km" / "6,2 km": one decimal, in the reader's number format (`locale` is
// `i18n.language`). The labels below take the caller's `t` and `locale` for the same reason.
// One formatter per locale and unit: these run for every card, row and map pin.
const unitFormats = new Map();
const unitFormat = (locale, unit) => {
  const key = `${locale}|${unit}`;
  if (!unitFormats.has(key)) {
    const digits = unit === 'kilometer' ? { minimumFractionDigits: 1, maximumFractionDigits: 1 } : {};
    unitFormats.set(key, new Intl.NumberFormat(locale, { style: 'unit', unit, ...digits }));
  }
  return unitFormats.get(key);
};
const formatKm = (km, locale) => unitFormat(locale, 'kilometer').format(km);

/**
 * "8 min walk" when it is walkable, "6.2 km" when it is not — the badge form, for a
 * list card that has room for two words.
 *
 * Minutes are the useful unit at the range where a person is deciding whether to go, and
 * a distance is the honest answer past it: how long the trip takes then depends on a metro
 * line this knows nothing about. Those are the only two forms — anything walkable reads as
 * minutes, so a card never quotes a distance in metres, whose precision a venue centroid
 * and a browser's location do not have between them anyway.
 */
export const shortDistanceLabel = (from, to, t, locale) => {
  const km = distanceKm(from, to);
  if (km === null) return null;

  const minutes = walkMinutes(from, to);
  return minutes === null ? formatKm(km, locale) : t('labels:misc.walkMinutes', { minutes });
};

/**
 * "350 m away · 4 min walk" for the detail sheet, or null without a user location.
 *
 * Walk time is dropped beyond a distance nobody walks — quoting "58 min walk" for
 * something across the city reads as a joke rather than as information.
 */
export const proximityLabel = (from, to, t, locale) => {
  const km = distanceKm(from, to);
  if (km === null) return null;

  const distance = km < 1
    ? unitFormat(locale, 'meter').format(Math.round((km * 1000) / 50) * 50)
    : formatKm(km, locale);
  const minutes = walkMinutes(from, to);
  return minutes === null ? distance : `${distance} · ${t('labels:misc.walkMinutes', { minutes })}`;
};
